// src/pages/driver_group/DriverGroupDetailsPage.js - Fixed to fetch full driver details
import React, { useState } from 'react';
import { useParams, useNavigate, Link as RouterLink } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  Box, 
  Typography, 
  Grid, 
  Paper, 
  Button, 
  Chip,
  Breadcrumbs,
  Link,
  Divider,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Snackbar,
  Alert,
  List,
  ListItem,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PeopleIcon from '@mui/icons-material/People';
import BusinessIcon from '@mui/icons-material/Business';
import PersonIcon from '@mui/icons-material/Person';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import { getDriverGroup, deleteDriverGroup, getDriverGroupDrivers } from '../../api/driverGroupAPI';
import { getDriver } from '../../api/driverAPI'; // Import individual driver fetcher
import { getCompany } from '../../api/companyAPI';
import { getTariff } from '../../api/tariffAPI';
import LoadingSpinner from '../../components/common/Loadingspinner';
import ErrorAlert from '../../components/common/ErrorAlert';

const DriverGroupDetailsPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  const driverGroupId = parseInt(id);

  // Fetch driver group details
  const { data: driverGroup, isLoading, isError, error } = useQuery({
    queryKey: ['driver-group', driverGroupId],
    queryFn: () => getDriverGroup(driverGroupId),
    enabled: !!driverGroupId && !isNaN(driverGroupId)
  });

  // Fetch company details once we have the driver group
  const { data: company, isLoading: isLoadingCompany } = useQuery({
    queryKey: ['company', driverGroup?.DriversGroupCompanyId],
    queryFn: () => getCompany(driverGroup.DriversGroupCompanyId),
    enabled: !!driverGroup?.DriversGroupCompanyId
  });

  // Fetch tariff details for this driver group
  const { data: tariff, isLoading: isLoadingTariff } = useQuery({
    queryKey: ['tariff', driverGroup?.DriverTariffId],
    queryFn: () => getTariff(driverGroup.DriverTariffId),
    enabled: !!driverGroup?.DriverTariffId,
    retry: 1
  });

  // Step 1: Fetch driver IDs in this group
  const { 
    data: driverIds,
    isLoading: isLoadingDriverIds,
    isError: isDriverIdsError,
    error: driverIdsError
  } = useQuery({
    queryKey: ['driver-group-drivers', driverGroupId],
    queryFn: () => getDriverGroupDrivers(driverGroupId),
    enabled: !!driverGroupId && !isNaN(driverGroupId)
  });

  // Step 2: Fetch full driver details for each driver ID
  const { 
    data: drivers,
    isLoading: isLoadingDrivers,
    isError: isDriversError,
    error: driversError
  } = useQuery({
    queryKey: ['drivers-details', driverIds],
    queryFn: async () => {
      if (!driverIds || driverIds.length === 0) {
        return [];
      }
      
      // Fetch all driver details in parallel
      const driverPromises = driverIds.map(driverId => getDriver(driverId));
      
      try {
        const driversData = await Promise.all(driverPromises);
        return driversData.filter(driver => driver !== null); // Filter out any failed requests
      } catch (error) {
        console.error('Error fetching driver details:', error);
        
        // If some drivers fail, try to fetch them individually and filter out failures
        const driversData = await Promise.allSettled(driverPromises);
        return driversData
          .filter(result => result.status === 'fulfilled')
          .map(result => result.value);
      }
    },
    enabled: !!driverIds && driverIds.length > 0,
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes to avoid refetching unnecessarily
  });

  // Combine loading states
  const isLoadingAll = isLoadingDriverIds || isLoadingDrivers;
  const isErrorAll = isDriverIdsError || isDriversError;
  const errorAll = driverIdsError || driversError;

  // Delete driver group mutation
  const deleteMutation = useMutation({
    mutationFn: deleteDriverGroup,
    onSuccess: () => {
      // Invalidate queries to refresh data
      if (driverGroup?.DriversGroupCompanyId) {
        queryClient.invalidateQueries({ queryKey: ['driver-groups', driverGroup.DriversGroupCompanyId] });
      }
      queryClient.invalidateQueries({ queryKey: ['driver-groups'] });
      
      setSnackbar({
        open: true,
        message: 'Driver group deleted successfully',
        severity: 'success'
      });
      
      // Navigate to driver groups page after deletion
      setTimeout(() => {
        navigate('/driver_groups');
      }, 1500);
    },
    onError: (error) => {
      setSnackbar({
        open: true,
        message: `Error deleting driver group: ${error.message}`,
        severity: 'error'
      });
    }
  });

  const handleDeleteClick = () => {
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      await deleteMutation.mutateAsync(driverGroupId);
    } catch (err) {
      // Error is handled by the mutation
    }
    setDeleteDialogOpen(false);
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (isError || !driverGroup) {
    return <ErrorAlert message={error instanceof Error ? error.message : 'Error loading driver group'} />;
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <div>
          <Typography variant="h4">{driverGroup.DriversGroupName}</Typography>
        </div>
        
        <Button 
          component={RouterLink} 
          to="/driver_groups" 
          startIcon={<ArrowBackIcon />}
        >
          Back to Driver Groups
        </Button>
      </Box>

      <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
          <Button
            variant="outlined"
            color="primary"
            startIcon={<EditIcon />}
            component={RouterLink}
            to={`/driver_groups/${driverGroup.DriversGroupId}/edit`}
            sx={{ mr: 1 }}
          >
            Edit
          </Button>
          <Button
            variant="outlined"
            color="error"
            startIcon={<DeleteIcon />}
            onClick={handleDeleteClick}
          >
            Delete
          </Button>
        </Box>
        
        <Divider sx={{ mb: 3 }} />
        
        <Grid container spacing={2}>
          {/* First Row */}
          <Grid item xs={12} sm={4}>
            <Typography variant="subtitle2" color="text.secondary">Group ID</Typography>
            <Typography variant="body1">{driverGroup.DriversGroupId}</Typography>
          </Grid>
          
          <Grid item xs={12} sm={4}>
            <Typography variant="subtitle2" color="text.secondary">Company</Typography>
            <Box display="flex" alignItems="center">
              <BusinessIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
              <Typography variant="body1">
                {company ? company.CompanyName : `Company ID: ${driverGroup.DriversGroupCompanyId}`}
              </Typography>
            </Box>
          </Grid>
          
          <Grid item xs={12} sm={4}>
            <Typography variant="subtitle2" color="text.secondary">Assigned Tariff</Typography>
            <Box display="flex" alignItems="center">
              <AttachMoneyIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
              <Typography variant="body1">
                {(() => {
                  if (isLoadingTariff) return 'Loading...';
                  if (!driverGroup.DriverTariffId) return 'No tariff assigned';
                  if (tariff) return `${tariff.TariffsName} (${tariff.TariffsType || 'No type'})`;
                  return `Tariff ID: ${driverGroup.DriverTariffId}`;
                })()}
              </Typography>
            </Box>
          </Grid>
          
          {/* Second Row */}
          <Grid item xs={12} sm={4}>
            <Typography variant="subtitle2" color="text.secondary">Status</Typography>
            <Chip 
              label={driverGroup.DriversGroupEnabled ? "Enabled" : "Disabled"} 
              color={driverGroup.DriversGroupEnabled ? "success" : "default"}
              size="small"
            />
          </Grid>
          
          <Grid item xs={12} sm={4}>
            <Typography variant="subtitle2" color="text.secondary">Created At</Typography>
            <Typography variant="body1">{new Date(driverGroup.DriversGroupCreated).toLocaleString()}</Typography>
          </Grid>
          
          <Grid item xs={12} sm={4}>
            <Typography variant="subtitle2" color="text.secondary">Last Updated</Typography>
            <Typography variant="body1">{new Date(driverGroup.DriversGroupUpdated).toLocaleString()}</Typography>
          </Grid>
        </Grid>
      </Paper>

      {/* Drivers List Section */}
      <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h6">
            <PeopleIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
            Drivers in this Group
            {drivers && drivers.length > 0 && (
              <Chip 
                label={`${drivers.length} driver${drivers.length > 1 ? 's' : ''}`} 
                size="small" 
                sx={{ ml: 2 }} 
              />
            )}
          </Typography>
          <Button 
            component={RouterLink} 
            to={`/drivers/new?group=${driverGroup.DriversGroupId}`} 
            variant="outlined"
          >
            Add Driver
          </Button>
        </Box>
        
        <Divider sx={{ mb: 2 }} />
        
        {/* Loading State */}
        {isLoadingAll && (
          <Box display="flex" justifyContent="center" py={3}>
            <LoadingSpinner />
          </Box>
        )}
        
        {/* Error State */}
        {isErrorAll && !isLoadingAll && (
          <ErrorAlert message={errorAll instanceof Error ? errorAll.message : 'Error loading drivers'} />
        )}
        
        {/* No Drivers State */}
        {!isLoadingAll && !isErrorAll && (!drivers || drivers.length === 0) && (
          <Typography variant="body1" color="text.secondary" textAlign="center" py={3}>
            No drivers found in this group
          </Typography>
        )}
        
        {/* Drivers List */}
        {!isLoadingAll && !isErrorAll && drivers && drivers.length > 0 && (
          <List>
            {drivers.map((driver) => (
              <ListItem 
                key={driver.DriverId}
                button
                component={RouterLink}
                to={`/drivers/${driver.DriverId}`}
                sx={{ 
                  borderBottom: '1px solid #eee',
                  '&:last-child': {
                    borderBottom: 'none'
                  }
                }}
              >
                <ListItemIcon>
                  <PersonIcon />
                </ListItemIcon>
                <ListItemText 
                  primary={driver.DriverFullName} 
                  secondary={
                    <>
                      ID: {driver.DriverId}
                      {driver.DriverEmail && ` • Email: ${driver.DriverEmail}`}
                      {driver.DriverPhone && ` • Phone: ${driver.DriverPhone}`}
                    </>
                  }
                />
                <Chip 
                  label={driver.DriverEnabled ? "Enabled" : "Disabled"} 
                  color={driver.DriverEnabled ? "success" : "default"}
                  size="small"
                />
              </ListItem>
            ))}
          </List>
        )}
        
        {/* Debug Info (you can remove this in production)
        {process.env.NODE_ENV === 'development' && (
          <Box mt={2} p={2} bgcolor="grey.100" borderRadius={1}>
            <Typography variant="caption" color="text.secondary">
              Debug Info: Driver IDs: {JSON.stringify(driverIds)} | 
              Drivers Count: {drivers?.length || 0} | 
              Loading IDs: {isLoadingDriverIds.toString()} | 
              Loading Drivers: {isLoadingDrivers.toString()}
            </Typography>
          </Box>
        )} */}
      </Paper>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>Delete Driver Group</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete "{driverGroup.DriversGroupName}"? This action cannot be undone.
            {drivers && drivers.length > 0 && (
              <Box mt={2} color="error.main">
                Warning: This group contains {drivers.length} driver(s). Deleting it may affect these drivers.
              </Box>
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} color="error" autoFocus>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar 
        open={snackbar.open} 
        autoHideDuration={6000} 
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default DriverGroupDetailsPage;